import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import { useBreakpointsMediaDown } from '@nubank/nuds-web/styles/breakpoints';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';

import Image from '@nubank/www-latam-commons/components/Image/Image';
import partition from '@nubank/www-latam-commons/utils/array/partition';
import InfiniteCarousel from 'components/InfiniteCarousel/InfiniteCarousel';

import tracking from '../../tracking';

import { BoletoIcon } from './components/BoletoIcon';
import { ChargeIcon } from './components/ChargeIcon';
import { DebitIcon } from './components/DebitIcon';
import { RechargeIcon } from './components/RechargeIcon';
import { SalaryIcon } from './components/SalaryIcon';
import { SavingsIcon } from './components/SavingsIcon';
import { WithdrawIcon } from './components/WithdrawIcon';

const IconsMap = {
  SavingsIcon,
  SalaryIcon,
  RechargeIcon,
  ChargeIcon,
  BoletoIcon,
  DebitIcon,
  WithdrawIcon,
};

function SectionBenefits({ index }) {
  const { formatMessage } = useContext(NuDsContext);

  const benefitsList = formatMessage({ id: `cms.accountPage.cardsArea[${index}].benefit` });

  const isMobile = useBreakpointsMediaDown('md');
  const partitionSize = isMobile ? 2 : 1;
  const [benefitsItens, setBenefitsItens] = useState(partition(benefitsList, partitionSize));

  useEffect(() => {
    setBenefitsItens(partition(benefitsList, partitionSize));
  }, [isMobile]);

  return (
    <Box tag="section">
      <Box paddingTop={{ lg: '12x' }} paddingHorizontal={{ lg: 56 }}>
        <Image
          altIntlKey="ACCOUNT.BENEFITS.IMAGE.ALT_TEXT"
          srcSet={{
            xs: {
              '1x': 'two-people-looking/two-people-looking-xs@1x.jpg',
              '2x': 'two-people-looking/two-people-looking-xs@2x.jpg',
              '3x': 'two-people-looking/two-people-looking-xs@3x.jpg',
            },
            md: {
              '1x': 'two-people-looking/two-people-looking-md@1x.jpg',
              '2x': 'two-people-looking/two-people-looking-md@2x.jpg',
              '3x': 'two-people-looking/two-people-looking-md@3x.jpg',
            },
            lg: {
              '1x': 'two-people-looking/two-people-looking-lg@1x.jpg',
              '2x': 'two-people-looking/two-people-looking-lg@2x.jpg',
              '3x': 'two-people-looking/two-people-looking-lg@3x.jpg',
            },
          }}
        />
      </Box>
      <Box paddingVertical={{ xs: '12x', md: 56, lg: '16x' }}>
        <Box
          display="grid"
          marginBottom={{ xs: '6x', md: '12x', lg: '16x' }}
          paddingHorizontal={{ xs: '6x', md: '12x' }}
          gridTemplateColumns={{ lg: 'repeat(12,1fr)' }}
          gridColumnGap={{ lg: '8x' }}
        >
          <Box gridColumn="2 / span 7">
            <Typography variant="title" intlKey={`cms.accountPage.cardsArea[${index}].title`} />
          </Box>
        </Box>
        <InfiniteCarousel
          cardSize={isMobile ? 280 : 312}
          initialOffset={isMobile ? 24 : 48}
          onInteraction={() => { tracking.carousel(); }}
        >
          {benefitsItens.map(itens => (
            <InfiniteCarousel.Item
              cardSize={isMobile ? 280 : 312}
              key={`carousel-item-${itens[0].id}`}
              display="flex"
              justifyContent="space-between"
              flexDirection="column"
            >
              {itens.map((item, itensIndex) => {
                const IconComponent = IconsMap[item.iconName];
                return (
                  <Box
                    key={itens[0].id}
                    padding="6x"
                    marginBottom={{ xs: itensIndex % 2 ? 0 : 32, md: 0 }}
                    border="1px solid"
                    borderColor="black.defaultT10"
                    borderRadius={12}
                    display="flex"
                    height={263}
                    flexDirection="column"
                    justifyContent="space-between"
                  >
                    <Box>
                      {IconComponent && <IconComponent />}
                      <Typography marginTop="2x" variant="subtitle">
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography marginTop="5x" variant="paragraph1">
                      {item.description}
                    </Typography>
                  </Box>
                );
              })}
            </InfiniteCarousel.Item>
          ))}
        </InfiniteCarousel>
      </Box>
    </Box>
  );
}

SectionBenefits.propTypes = {
  index: PropTypes.number.isRequired,
};

export default React.memo(SectionBenefits);
